import {
  addListener,
  type UnknownAction,
  type Unsubscribe,
} from "@reduxjs/toolkit";

import type { AppDispatch } from "@src/store/rootState";

/**
 * Resolves with the first dispatched action that matches, or null once
 * `timeoutMs` passes or `signal` aborts. A one-shot listener: it removes
 * itself either way, so a wait that is never answered holds nothing.
 */
export const waitForAction = <A extends UnknownAction>(
  dispatch: AppDispatch,
  predicate: (action: UnknownAction) => action is A,
  timeoutMs: number,
  signal: AbortSignal
): { result: Promise<A | null>; cancel: () => void } => {
  let unsubscribe: Unsubscribe = () => undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let settle: (action: A | null) => void = () => undefined;
  const result = new Promise<A | null>((resolve) => {
    settle = (action) => {
      clearTimeout(timer);
      unsubscribe();
      resolve(action);
    };
  });
  unsubscribe = dispatch(
    addListener({ predicate, effect: (action) => settle(action as A) })
  ) as unknown as Unsubscribe;
  timer = setTimeout(() => settle(null), timeoutMs);
  if (signal.aborted) {
    settle(null);
  } else {
    signal.addEventListener("abort", () => settle(null), { once: true });
  }
  return { result, cancel: () => settle(null) };
};
